import { ArrowLeft, ArrowRight } from "lucide-react";
import beOneLogo from "../../assets/b1-logo-icon.svg";
import { getThemeFeedback } from "../data/feedbackData";
import { useLocale, useT } from "../i18n";
import { ProgressiveRadarChart } from "./ProgressiveRadarChart";

interface ThemeCompleteProps {
  themeNumber: number;
  themeName: string;
  onContinue: () => void;
  onGoBack: () => void;
  allThemeScores: {
    strategie: number;
    marque: number;
    contenu: number;
    ia: number;
  };
  answers: Record<number, string>;
}

export function ThemeComplete({
  themeNumber,
  themeName,
  onContinue,
  onGoBack,
  allThemeScores,
  answers,
}: ThemeCompleteProps) {
  const t = useT();
  const locale = useLocale();
  const feedback = getThemeFeedback(themeNumber, answers, locale);

  return (
    <div
      className="min-h-screen p-6"
      style={{
        background: "linear-gradient(to bottom right, #02324A, #00AFFD)",
      }}
    >
      <div className="max-w-3xl mx-auto p-0">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <img
            src={beOneLogo}
            alt="Be-one"
            width={96}
            height={96}
            className="h-24 mx-auto mb-4"
          />
          <h2 className="font-[Roboto_Slab] text-white text-2xl mb-2">
            {t("themeComplete.title", { num: themeNumber })}
            <br />
            {themeName}
          </h2>
        </div>

        {/* Top Continue Button */}
        <button
          type="button"
          onClick={onContinue}
          className="w-full text-white rounded-lg flex items-center justify-center gap-2 transition-colors font-[Roboto] text-lg font-medium px-[24px] py-[12px] mb-8"
          style={{ backgroundColor: "#FF00D2" }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#D900B0")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "#FF00D2")
          }
        >
          {t("themeComplete.continue", { next: themeNumber + 1 })}
          <ArrowRight className="w-5 h-5" />
        </button>

        {/* Theme Feedback */}
        <div className="text-center rounded-lg bg-white/10 backdrop-blur-sm p-[24px] mb-8">
          {feedback.split("\n\n").map((paragraph, i) => (
            <p
              key={paragraph}
              className={`font-[Roboto] leading-relaxed text-white${i > 0 ? " mt-4" : ""}`}
            >
              {paragraph}
            </p>
          ))}
        </div>

        {/* Radar Chart */}
        <div className="mb-8">
          <ProgressiveRadarChart
            completedThemes={themeNumber}
            scores={allThemeScores}
          />
        </div>

        {/* Continue Button */}
        <button
          type="button"
          onClick={onContinue}
          className="w-full text-white rounded-lg flex items-center justify-center gap-2 transition-colors font-[Roboto] text-lg font-medium px-[24px] py-[12px] m-0"
          style={{ backgroundColor: "#FF00D2" }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#D900B0")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "#FF00D2")
          }
        >
          {t("themeComplete.continue", { next: themeNumber + 1 })}
          <ArrowRight className="w-5 h-5" />
        </button>

        <div className="mt-6">
          <button
            type="button"
            onClick={onGoBack}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors font-[Roboto] text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("themeComplete.previous")}
          </button>
        </div>
      </div>
    </div>
  );
}
